import React, { useCallback, useContext, useState } from 'react';
import { connect } from 'react-redux';
import CheckIcon from 'mdi-react/CheckboxMarkedCircleIcon';
import ErrorIcon from 'mdi-react/CloseCircleIcon';
import { AppContext } from '@prisjakt/ui-app-context';
import { usePriceRowContext } from '../../../../price-row-context';
import { colors } from '@prisjakt/ui-base';
import { priceLabel } from 'src/components/price-label/types';
import { Spinner } from 'src/components/spinner';
import StoreLogo, { VARIANTS } from '@prisjakt/ui-store-logo';
import { reportWrongPrice as reportWrongPriceAction } from '../../../../../../../feedback/actions';
import { postFeedback as feedbackRequest } from '../../../../../../../../globals/api/requests';
import Modal from '../../../../../../../../components/modal';
import StaticIntercom from '../../../../../../../../components/static-intercom';
import { StockStatus } from 'src/types';
import { Metadata, ReportType } from '../types';
import { SHOP_LOGO_WIDTH } from '../../../../../../../store/constants';
import * as S from './styles';

type OwnProps = {
  closeModal: () => void;
  isOpen: boolean;
  onClose?: () => void;
  priceId?: string | null;
  priceValue?: number | null;
  productName?: string | null;
  stockStatus?: StockStatus | null;
};

type DispatchProps = {
  reportWrongPriceDispatch: (metadata: Metadata) => {};
};

type Props = OwnProps & DispatchProps;

const ReportModal: React.FC<Props> = props => {
  const {
    closeModal,
    isOpen,
    onClose,
    priceId,
    priceValue,
    productName,
    reportWrongPriceDispatch,
    stockStatus,
  } = props;
  const { productImage, offer } = usePriceRowContext();
  const { store } = offer;
  const { market, t } = useContext(AppContext);

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [sending, setSending] = useState(false);

  const submitted = success || error || sending;

  const postFeedback = async (data: Metadata) => {
    const res = await feedbackRequest(data);

    if (res.data.feedback.success === true) {
      return res;
    }
    if (res.errors) {
      throw new Error(
        `Error creating report on price: ${JSON.stringify(res.errors)}`
      );
    }

    throw new Error('Error creating report on price.');
  };

  const onReport = useCallback(
    (e: React.MouseEvent, type: ReportType) => {
      if (!IS_BROWSER) return;

      // Stops click out action event
      e.preventDefault();
      e.stopPropagation();

      const message = `Error report on ${window.location.href}`;

      const metadata = {
        message,
        agent_type_key: type,
        uri: window.location.href,
        country_code: market.code,
        store_id: store && store.id,
        price_id: parseInt(priceId as string, 10),
        price_value: String(priceValue),
        stock_status: stockStatus,
      } as Metadata;

      reportWrongPriceDispatch(metadata); // Action event for tracking

      setSending(true);
      postFeedback(metadata)
        .then(() => {
          setSending(false);
          setSuccess(true);
        })
        .catch(() => {
          setSending(false);
          setError(true);
        })
        .then(() => {
          setTimeout(() => {
            setSuccess(false);
            return onClose ? onClose() : closeModal();
          }, 3000);
        });
    },
    [
      market.code,
      store,
      priceId,
      priceValue,
      stockStatus,
      reportWrongPriceDispatch,
      onClose,
      closeModal,
    ]
  );

  const reportWrongPrice = useCallback(
    (e: React.MouseEvent) => onReport(e, ReportType.WrongPrice),
    [onReport]
  );
  const reportWrongProduct = useCallback(
    (e: React.MouseEvent) => onReport(e, ReportType.WrongProduct),
    [onReport]
  );
  const reportWrongStockStatus = useCallback(
    (e: React.MouseEvent) => onReport(e, ReportType.WrongStockStatus),
    [onReport]
  );

  const highResolutionLogo = store?.logo
    ? { url: store.logo, width: SHOP_LOGO_WIDTH }
    : null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      shouldShowCloseIcon={!submitted}
      width={1024}
    >
      <S.Content>
        {submitted ? (
          <S.SubmitMessage>
            {sending && <Spinner isActive position="none" size={30} />}

            {success && (
              <S.Message>
                <CheckIcon color={colors.lightTheme.positive500} size="44" />
                {t('feedback', 'dialog.thanks-for-feedback')}
              </S.Message>
            )}
            {error && (
              <S.Message>
                <ErrorIcon color={colors.lightTheme.negative700} size="44" />
                {t('feedback', 'dialog.request-error')}
              </S.Message>
            )}
          </S.SubmitMessage>
        ) : (
          <>
            <S.Title>
              {store && store.hasLogo && (
                <StoreLogo
                  name={store.name}
                  storeId={store.id}
                  featured={Boolean(store.featured)}
                  hasImage={store.hasLogo}
                  variant={VARIANTS.PRICE_LIST}
                  logoUrl={highResolutionLogo?.url}
                  width={highResolutionLogo?.width}
                  showHighResolutionLogo={Boolean(highResolutionLogo)}
                />
              )}
            </S.Title>

            <S.Card>
              {productImage && (
                <S.ProductImage src={productImage} name={productName} />
              )}
              <S.ProductName>{productName}</S.ProductName>
              <S.ReportDescription thin>
                {t('feedback', 'report.wrong-product-description')}
              </S.ReportDescription>
              <S.Button onClick={reportWrongProduct} fullWidth>
                {t('feedback', 'report.wrong-product')}
              </S.Button>
            </S.Card>

            <S.Card>
              <S.PriceLabel value={priceValue} styling={priceLabel.PRIMARY} />
              <S.ReportDescription thin>
                {t('feedback', 'report.wrong-price-description')}
              </S.ReportDescription>

              <S.Button onClick={reportWrongPrice} fullWidth>
                {t('feedback', 'report.wrong-price')}
              </S.Button>
            </S.Card>

            <S.Card>
              <S.StockStatus status={stockStatus} size={46} />
              <S.ReportDescription thin>
                {t('feedback', 'report.wrong-stock-status-description')}
              </S.ReportDescription>
              <S.Button onClick={reportWrongStockStatus} fullWidth>
                {t('feedback', 'report.wrong-stock-status')}
              </S.Button>
            </S.Card>

            <S.Card>
              <S.ReportDescription>
                {t('feedback', 'report.other-feedback-description')}
              </S.ReportDescription>

              <StaticIntercom
                customButton={
                  <S.FeedbackButton
                    containerProps={{
                      title: t('feedback', 'report.other-feedback'),
                    }}
                    fullWidth
                  >
                    {t('feedback', 'report.other-feedback')}
                  </S.FeedbackButton>
                }
              />
            </S.Card>
          </>
        )}
      </S.Content>
    </Modal>
  );
};

const dispatchProps = {
  reportWrongPriceDispatch: reportWrongPriceAction,
};

export default connect(null, dispatchProps)(ReportModal);
